import { useMemo, useState } from 'react'
import { Trophy, CheckCircle2, Circle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import BracketTreeView, { ParticipantName } from './BracketTreeView'
import { tournamentsAPI } from '@/lib/api'
import { cn } from '@/lib/utils'

/* Group Stage + Knockout — per-groep round-robin tabellen + knockout-tree.
   Knockout wordt aangemaakt via admin-actie zodra de group-stage volledig af is. */
export default function GroupKOBracket({ tournamentId, matches, participantMap, participantHrefs = {}, onMatchClick, isAdmin }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const groupMatches = matches.filter(m => m.phase === 'group')
  const koMatches    = matches.filter(m => m.phase === 'knockout')

  const groups = useMemo(() => {
    const g = {}
    groupMatches.forEach(m => { (g[m.group_number] ||= []).push(m) })
    return g
  }, [groupMatches])

  const allGroupDone = groupMatches.length > 0 && groupMatches.every(m => m.status === 'completed')
  const koExists = koMatches.length > 0

  const startKO = async () => {
    setBusy(true); setError(null)
    try { await tournamentsAPI.startGroupKnockout(tournamentId) }
    catch (e) { setError(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-muted-foreground">
            {koExists
              ? <span className="text-live">Knockout-fase actief.</span>
              : allGroupDone
                ? <span className="text-acid">Group-stage af — je kan de knockout starten.</span>
                : <span>Wacht tot alle group-matches af zijn voor de knockout.</span>}
          </div>
          {!koExists && (
            <Button size="sm" disabled={!allGroupDone || busy}
                    variant={allGroupDone ? 'default' : 'outline'}
                    onClick={startKO}>
              {busy ? 'Bezig…' : 'Start knockout-fase'}
            </Button>
          )}
        </div>
      )}
      {error && <div className="text-sm text-signal">{error}</div>}

      {/* Groepstabellen */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Object.keys(groups).sort((a, b) => +a - +b).map(g => (
          <GroupTable key={g} groupNumber={+g} matches={groups[g]} participantMap={participantMap} participantHrefs={participantHrefs}
                      onMatchClick={onMatchClick} isAdmin={isAdmin} />
        ))}
      </div>

      {/* Knockout-tree (indien gegenereerd) */}
      {koExists && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-acid">
              Knockout-fase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BracketTreeView matches={koMatches} onMatchClick={onMatchClick} isAdmin={isAdmin}
                             participantHrefs={participantHrefs} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function GroupTable({ groupNumber, matches, participantMap, participantHrefs = {}, onMatchClick, isAdmin }) {
  // Compute per-participant stats binnen deze groep.
  const stats = useMemo(() => {
    const s = new Map()
    const addRow = (pid) => {
      if (!pid) return
      if (!s.has(pid)) s.set(pid, { participant_id: pid, wins: 0, losses: 0, games: 0 })
    }
    matches.forEach(m => {
      addRow(m.participant1_id); addRow(m.participant2_id)
      if (m.status !== 'completed' || !m.winner_id) return
      const w = s.get(m.winner_id)
      const lId = m.winner_id === m.participant1_id ? m.participant2_id : m.participant1_id
      const l = s.get(lId)
      if (w) { w.wins++; w.games++ }
      if (l) { l.losses++; l.games++ }
    })
    return Array.from(s.values())
      .sort((a, b) => b.wins - a.wins || a.losses - b.losses)
  }, [matches])

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-mono uppercase tracking-widest">
          Groep {groupNumber}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/40">
                <th className="text-left py-1 pr-2">Team</th>
                <th className="text-right py-1 px-1">W</th>
                <th className="text-right py-1 px-1">V</th>
                <th className="text-right py-1 pl-2">G</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={s.participant_id} className={cn(
                  'border-b border-border/20 last:border-0',
                  i === 0 && 'text-acid font-semibold'
                )}>
                  <td className="py-1 pr-2 truncate max-w-[140px]">
                    <ParticipantName name={participantMap[s.participant_id] || `#${s.participant_id}`}
                                     href={participantHrefs[s.participant_id]} />
                  </td>
                  <td className="py-1 px-1 text-right tabular-nums">{s.wins}</td>
                  <td className="py-1 px-1 text-right tabular-nums text-muted-foreground">{s.losses}</td>
                  <td className="py-1 pl-2 text-right tabular-nums text-muted-foreground">{s.games}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-1">
          {matches.map(m => (
            <MiniMatch key={m.id} match={m} participantMap={participantMap} participantHrefs={participantHrefs}
                       onClick={() => isAdmin && m.participant1_id && m.participant2_id && onMatchClick && onMatchClick(m)}
                       clickable={isAdmin && m.participant1_id && m.participant2_id} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function MiniMatch({ match, participantMap, participantHrefs = {}, onClick, clickable }) {
  const done = match.status === 'completed'
  const chip = done ? <CheckCircle2 className="h-3 w-3 text-live" />
              : match.status === 'in_progress' ? <Clock className="h-3 w-3 text-live" />
              : <Circle className="h-3 w-3 text-muted-foreground" />
  const p1 = participantMap[match.participant1_id] || '?'
  const p2 = participantMap[match.participant2_id] || '?'
  return (
    <div onClick={clickable ? onClick : undefined}
         className={cn('flex items-center gap-2 text-xs px-2 py-1 rounded border border-border/40',
                       clickable && 'cursor-pointer hover:border-acid')}>
      {chip}
      <ParticipantName name={p1} href={participantHrefs[match.participant1_id]}
                       className={cn('flex-1 truncate', !!match.winner_id && match.winner_id === match.participant1_id && 'font-semibold text-live')} />
      <span className="text-muted-foreground">vs</span>
      <ParticipantName name={p2} href={participantHrefs[match.participant2_id]}
                       className={cn('flex-1 truncate text-right', !!match.winner_id && match.winner_id === match.participant2_id && 'font-semibold text-live')} />
      {match.score && <span className="font-mono tabular-nums text-muted-foreground">{match.score}</span>}
    </div>
  )
}
